import React, { useCallback, useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { Cart, Dash, Plus, Trash } from 'react-bootstrap-icons';

import useCartContant from './useCartContant';
import LoginModal from './LoginModal';
import Button from '../common/Button';
import { EmptyState, ErrorState, InlineLoader } from '../common/States';
import { apiUrl, assetUrl } from '../../config/api';
import { formatPrice, toNumber } from '../../config/format';
import './Cart.scss';

const CartModal = ({ show, onClose }) => {
  const navigate = useNavigate();
  const { cartproducts, loading, error, fetchCartProducts } = useCartContant();

  const [loginShow, setLoginShow] = useState(false);
  const [busyKey, setBusyKey] = useState(null);
  const [clearing, setClearing] = useState(false);

  useEffect(() => {
    if (show) fetchCartProducts();
  }, [show, fetchCartProducts]);

  const items = cartproducts?.cart_items || [];
  const hasItems = items.length > 0;

  const identity = () => ({
    user_id: parseInt(localStorage.getItem('user_id') || '', 10),
    common_id: localStorage.getItem('common_id') || '',
  });

  const updateQuantity = async (variantId, productCode, action) => {
    setBusyKey(`${variantId}-${action}`);
    try {
      const response = await fetch(apiUrl('/api/cart/quantity'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...identity(),
          variant_id: variantId,
          product_code: productCode,
          action,
        }),
      });
      if (!response.ok) throw new Error('Network response was not ok');
      await fetchCartProducts();
    } catch {
      // fetchCartProducts surfaces the error state.
    } finally {
      setBusyKey(null);
    }
  };

  const removeProduct = async (variantId, productCode) => {
    setBusyKey(`remove-${variantId}`);
    try {
      const response = await fetch(apiUrl('/api/cart/delete'), {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...identity(),
          product_code: productCode,
          variant_id: variantId,
        }),
      });
      if (!response.ok) throw new Error('Network response was not ok');
      await fetchCartProducts();
    } catch {
      /* handled by the cart error state */
    } finally {
      setBusyKey(null);
    }
  };

  const clearCart = async () => {
    setClearing(true);
    try {
      const response = await fetch(apiUrl('/api/cart/delete-all'), {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(identity()),
      });
      if (!response.ok) throw new Error('Network response was not ok');
      await fetchCartProducts();
    } catch {
      /* handled by the cart error state */
    } finally {
      setClearing(false);
    }
  };

  const handleCheckout = useCallback(() => {
    onClose();
    if (!localStorage.getItem('token') || !localStorage.getItem('user_id')) {
      setLoginShow(true);
    } else {
      navigate('/check/checkoutpage');
    }
  }, [navigate, onClose]);

  const couponApplied =
    cartproducts?.coupon_applied !== 'N/A' ? cartproducts?.coupon_applied : null;

  return (
    <>
      <Modal show={show} onHide={onClose} size="lg" scrollable centered>
        <Modal.Header closeButton>
          <span>
            Your cart
            {hasItems && (
              <span className="ij-cart__count">
                {items.length} {items.length === 1 ? 'item' : 'items'}
              </span>
            )}
          </span>
        </Modal.Header>

        <Modal.Body>
          {loading && !hasItems ? (
            <InlineLoader label="Loading your cart" />
          ) : error ? (
            <ErrorState description={error} onRetry={() => fetchCartProducts()} />
          ) : !hasItems ? (
            <EmptyState
              icon={<Cart size={26} aria-hidden="true" />}
              title="Your cart is empty"
              description="Add a piece you love and it will show up here."
              action={
                <Button
                  variant="primary"
                  onClick={() => {
                    onClose();
                    navigate('/allproducts');
                  }}
                >
                  Start shopping
                </Button>
              }
            />
          ) : (
            <div className="ij-cart">
              <ul className="ij-cart__items">
                {items.map((item) => {
                  const variantId = item?.variant?.id;
                  const productCode = item?.product?.product_code;
                  const variantLabel = [item?.variant?.size, item?.variant?.pieces && `${item.variant.pieces} pieces`]
                    .filter(Boolean)
                    .join(' · ');

                  return (
                    <li className="ij-cart__item" key={`${productCode}-${variantId}`}>
                      <img
                        className="ij-cart__thumb"
                        src={assetUrl(item?.product?.product_image_1)}
                        alt=""
                        loading="lazy"
                      />

                      <div className="ij-cart__details">
                        <p className="ij-cart__name">{item?.product?.product_name}</p>
                        {variantLabel && (
                          <p className="ij-caption">{variantLabel}</p>
                        )}
                        <p className="ij-cart__line-price">
                          {formatPrice(item?.product_total_price)}
                        </p>

                        <div className="ij-qty ij-qty--sm">
                          <button
                            type="button"
                            aria-label="Decrease quantity"
                            disabled={busyKey !== null}
                            onClick={() =>
                              updateQuantity(variantId, productCode, 'decrement')
                            }
                          >
                            <Dash size={16} aria-hidden="true" />
                          </button>
                          <output aria-live="polite">
                            {item?.product_cart_quantity}
                          </output>
                          <button
                            type="button"
                            aria-label="Increase quantity"
                            disabled={busyKey !== null}
                            onClick={() =>
                              updateQuantity(variantId, productCode, 'increment')
                            }
                          >
                            <Plus size={16} aria-hidden="true" />
                          </button>
                        </div>

                        {item?.adjustment_message && (
                          <p className="ij-cart__notice">
                            {item.adjustment_message}
                          </p>
                        )}
                      </div>

                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={`Remove ${item?.product?.product_name}`}
                        loading={busyKey === `remove-${variantId}`}
                        onClick={() => removeProduct(variantId, productCode)}
                      >
                        <Trash size={16} aria-hidden="true" />
                      </Button>
                    </li>
                  );
                })}
              </ul>

              <aside className="ij-cart__summary" aria-label="Order summary">
                <h3 className="ij-cart__summary-title">Summary</h3>

                <dl className="ij-cart__rows">
                  <div>
                    <dt>Subtotal</dt>
                    <dd>{formatPrice(cartproducts.cart_sum)}</dd>
                  </div>

                  {couponApplied?.discount_coupon && (
                    <>
                      <div className="ij-cart__row--positive">
                        <dt>
                          Coupon {couponApplied.discount_coupon} (
                          {couponApplied.discount_percent}%)
                        </dt>
                        <dd>applied</dd>
                      </div>
                      <div>
                        <dt>After coupon</dt>
                        <dd>{formatPrice(cartproducts.discounted_price)}</dd>
                      </div>
                    </>
                  )}

                  {cartproducts?.tax_percent !== undefined && (
                    <div>
                      <dt>Tax</dt>
                      <dd>{toNumber(cartproducts.tax_percent)}%</dd>
                    </div>
                  )}

                  <div className={cartproducts?.free_shipping ? 'ij-cart__row--positive' : ''}>
                    <dt>Shipping</dt>
                    <dd>
                      {cartproducts?.free_shipping
                        ? 'Free'
                        : formatPrice(cartproducts?.standard_shipping_price)}
                    </dd>
                  </div>
                </dl>

                <div className="ij-cart__total">
                  <span>Total</span>
                  <span>{formatPrice(cartproducts.subtotal_after_tax)}</span>
                </div>

                <Button variant="primary" block onClick={handleCheckout}>
                  Checkout
                </Button>
                <Button
                  variant="ghost"
                  block
                  loading={clearing}
                  loadingText="Clearing…"
                  onClick={clearCart}
                >
                  Clear cart
                </Button>
              </aside>
            </div>
          )}
        </Modal.Body>
      </Modal>

      <LoginModal
        checkout
        show={loginShow}
        onClose={() => setLoginShow(false)}
      />
    </>
  );
};

export default CartModal;
